import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { toast } from 'react-toastify';
import { TaskInput } from '~/generated/graphql';
import { JobTemplateFieldsFragment } from '~/gql/graphql';
import {
  getTemplateBehavior,
  isAssetDrivenTemplate,
} from '../AssetDrivenTemplateIndicator';
import { normalizeTemplateName } from './normalizeTemplateName';

type TemplateManagerProps<T extends FieldValues = any> = {
  form: {
    watch: UseFormWatch<T>;
    setValue: UseFormSetValue<T>;
    trigger: UseFormTrigger<T>;
    getValues: UseFormGetValues<T>;
  };
  taskState: [TaskInput[], React.Dispatch<React.SetStateAction<TaskInput[]>>];
  isEditMode?: boolean;
  taskConverter?: (templateTask: any) => TaskInput;
  /** Callback when an asset-driven template is selected */
  onAssetDrivenTemplateSelect?: (template: JobTemplateFieldsFragment) => void;
};

export const useTemplateManager = <T extends FieldValues>({
  form,
  taskState,
  isEditMode = false,
  taskConverter,
  onAssetDrivenTemplateSelect,
}: TemplateManagerProps<T>) => {
  const { watch, setValue, trigger } = form;
  const [tasks, setTasks] = taskState;
  const [selectedTemplate, setSelectedTemplate] =
    useState<JobTemplateFieldsFragment | null>(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [templateTagIds, setTemplateTagIds] = useState<string[]>([]);
  const [templateAttributeIds, setTemplateAttributeIds] = useState<string[]>(
    []
  );
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [initialName, setInitialName] = useState<string>('');
  const [initialTags, setInitialTags] = useState<string[]>([]);
  const [initialAttributes, setInitialAttributes] = useState<string[]>([]);

  /**
   * Check if the currently selected template is asset-driven
   */
  const isSelectedTemplateAssetDriven = useCallback(() => {
    if (!selectedTemplate) return false;
    const behavior = getTemplateBehavior(selectedTemplate);
    return isAssetDrivenTemplate(behavior);
  }, [selectedTemplate]);

  // Use refs instead of state for tracking initial values
  const initialValuesRef = useRef({
    name: '',
    tags: [] as string[],
    attributes: [] as string[],
    notes: '',
  });

  const resetInitialValues = useCallback(() => {
    const currentName = watch('name' as Path<T>) || '';
    const currentTags = (watch('tags' as Path<T>) || []) as string[];
    const currentAttributes = (watch('includeAttributes' as Path<T>) ||
      []) as string[];
    const currentNotes = watch('notes' as Path<T>) || '';

    initialValuesRef.current = {
      name: currentName,
      tags: Array.isArray(currentTags) ? [...currentTags] : [],
      attributes: Array.isArray(currentAttributes)
        ? [...currentAttributes]
        : [],
      notes: currentNotes,
    };
  }, [watch]); // Fixed: depend on watch directly instead of form object to prevent infinite loops

  // Compute active tasks count on demand
  const getActiveTasksCount = () => {
    return tasks.filter((task) => !task._destroy).length;
  };
  const hasExistingData = () => {
    const currentName = watch('name' as Path<T>);

    // Get active tasks count
    const currentTasks = getActiveTasksCount() > 0;

    return Boolean(currentTasks);
  };

  const arraysEqual = (arr1: any[] = [], arr2: any[] = []) => {
    if (arr1?.length !== arr2?.length) return false;
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }
    return true;
  };

  const isTemplateModified = (template: JobTemplateFieldsFragment) => {
    const currentName = watch('name' as any);
    const currentNotes = watch('notes' as any);
    const currentTags: string[] = (watch('tags' as any) || []) as string[];
    const currentAttributes: string[] = (watch('includeAttributes' as any) ||
      []) as string[];

    // Get active tasks count
    const currentTaskCount = getActiveTasksCount();

    const templateName = normalizeTemplateName(template.name);
    const templateNotes = template.notes || '';
    const templateTags = (template.tags?.map(({ id }) => id) || []) as string[];
    const templateAttributes = (template.includeAttributes || []) as string[];
    const templateTasks = template.tasks || [];
    const templateTaskCount = templateTasks?.length ?? 0;

    // Check if this is an asset-driven template
    const templateBehavior = getTemplateBehavior(template);
    const isAssetDriven = isAssetDrivenTemplate(templateBehavior);

    // In edit mode, we need different comparison logic
    if (isEditMode) {
      // Check if all template tags are still present
      const allTemplateTagsPresent =
        templateTags.length === 0
          ? true
          : templateTags.every(
              (tagId: string) =>
                Array.isArray(currentTags) && currentTags.includes(tagId)
            );

      // Check if all template attributes are still present
      const allTemplateAttributesPresent =
        templateAttributes.length === 0
          ? true
          : templateAttributes.every(
              (attrId: string) =>
                Array.isArray(currentAttributes) &&
                currentAttributes.includes(attrId)
            );

      // For asset-driven templates, skip task count comparison since tasks are generated dynamically
      // For regular templates, check if tasks match the template (using count as a simple heuristic)
      const tasksModified = isAssetDriven
        ? false
        : currentTaskCount !== templateTaskCount;

      // In edit mode, consider template modified if tags/attributes were removed or tasks modified
      const isModified =
        !allTemplateTagsPresent ||
        !allTemplateAttributesPresent ||
        tasksModified;

      return isModified;
    } else {
      // Create mode logic
      // For asset-driven templates, skip task count comparison since tasks are generated dynamically
      const tasksModified = isAssetDriven
        ? false
        : currentTaskCount !== templateTaskCount;

      const isModified =
        currentName !== templateName ||
        currentNotes !== templateNotes ||
        !arraysEqual(currentTags, templateTags) ||
        !arraysEqual(currentAttributes, templateAttributes) ||
        tasksModified;

      return isModified;
    }
  };

  const applyTemplate = useCallback(
    async (
      template: JobTemplateFieldsFragment,
      options?: { skipTasks?: boolean; forceTemplateName?: boolean }
    ) => {
      try {
        // Make sure to call resetInitialValues before applying the template
        resetInitialValues();

        const templateBehavior = getTemplateBehavior(template);
        const isAssetDriven = isAssetDrivenTemplate(templateBehavior);

        // For asset-driven templates, skip task application by default
        // Tasks will be generated later based on selected assets via applyAssetDrivenTemplateTasks
        // This prevents double-application: applyTemplate clears tasks, then applyAssetDrivenTemplateTasks
        // generates new tasks from the template based on selected assets
        const shouldSkipTasks = options?.skipTasks ?? isAssetDriven;

        const currentName = watch('name' as any) || '';

        // Update the name if it's empty OR if forceTemplateName is true (for bulk asset template creation)
        if (!currentName || options?.forceTemplateName) {
          // Clean template name
          const cleanTemplateName = normalizeTemplateName(template.name);

          setValue('name' as any, cleanTemplateName as any);
        }

        if (template.tags && template.tags.length > 0) {
          // Track the tag IDs from the template for potential removal later
          const newTemplateTagIds = template.tags.map(({ id }) => id);
          setTemplateTagIds(newTemplateTagIds);

          // Get current tags
          const currentTags = watch('tags' as any) || [];

          // Always combine existing tags with template tags (no duplicates)

          if (Array.isArray(currentTags)) {
            const combinedTags = [
              ...new Set([...currentTags, ...newTemplateTagIds]),
            ];
            setValue('tags' as any, combinedTags as any);
          } else {
            setValue('tags' as any, newTemplateTagIds as any);
          }
        }

        if (template.notes) {
          setValue('notes' as any, template.notes as any);
        }

        if (
          template.includeAttributes &&
          template.includeAttributes.length > 0
        ) {
          // Track the attribute IDs from the template for potential removal later
          const newTemplateAttributeIds = [...template.includeAttributes];
          setTemplateAttributeIds(newTemplateAttributeIds);

          // Get current attributes
          const currentAttributes = watch('includeAttributes' as any) || [];

          // Always combine existing attributes with template attributes (no duplicates)
          if (Array.isArray(currentAttributes)) {
            const combinedAttributes = [
              ...new Set([...currentAttributes, ...newTemplateAttributeIds]),
            ];
            setValue('includeAttributes' as any, combinedAttributes as any);
          } else {
            setValue(
              'includeAttributes' as any,
              newTemplateAttributeIds as any
            );
          }
        }

        // Handle tasks - skip for asset-driven templates
        if (!shouldSkipTasks) {
          if (template.tasks && template.tasks.length > 0 && taskConverter) {
            // Create tasks with unique IDs
            const newTemplateId = `template-${Date.now()}`;
            setTemplateId(newTemplateId);

            const newTasks = template.tasks.map((templateTask, index) => {
              // Use the provided taskConverter function
              const task = taskConverter(templateTask);
              return { ...task, id: `${newTemplateId}-task-${index}` };
            });

            setTasks(newTasks);
          }
        } else {
          // Clear any existing tasks when applying an asset-driven template;
          // tasks will be generated once assets are selected.
          setTasks([]);
          setValue('tasks' as any, [] as any);
        }

        // Trigger validation after setting values
        await trigger('name' as Path<T>);
        setSelectedTemplate(template);

        // Notify parent if asset-driven template was selected
        if (isAssetDriven && onAssetDrivenTemplateSelect) {
          onAssetDrivenTemplateSelect(template);
        }
      } catch (error) {
        toast.error('Failed to apply template. Please try again.');
      }
    },
    [
      resetInitialValues,
      watch,
      setValue,
      trigger,
      taskConverter,
      setTasks,
      setTemplateTagIds,
      setTemplateAttributeIds,
      setTemplateId,
      setSelectedTemplate,
      onAssetDrivenTemplateSelect,
    ]
  );

  const removeTemplate = useCallback(() => {
    const currentName = watch('name' as any) || '';
    const templateName = normalizeTemplateName(selectedTemplate?.name);

    if (currentName === templateName) {
      setValue('name' as any, initialValuesRef.current.name as any);
    }

    // Use ref values for tags
    const currentTags = (watch('tags' as any) || []) as string[];
    if (Array.isArray(currentTags)) {
      const baseTags = [...initialValuesRef.current.tags];
      const nonTemplateTags = currentTags.filter(
        (tagId: string) => !templateTagIds.includes(tagId)
      );
      const finalTags = [...new Set([...baseTags, ...nonTemplateTags])];
      setValue('tags' as any, finalTags as any);
    }

    // Use ref values for attributes
    const currentAttributes = (watch('includeAttributes' as any) ||
      []) as string[];
    if (Array.isArray(currentAttributes)) {
      const baseAttributes = [...initialValuesRef.current.attributes];
      const nonTemplateAttributes = currentAttributes.filter(
        (attrId: string) => !templateAttributeIds.includes(attrId)
      );
      const finalAttributes = [
        ...new Set([...baseAttributes, ...nonTemplateAttributes]),
      ];
      setValue('includeAttributes' as any, finalAttributes as any);
    }

    // Only reset notes if they match the template notes
    const currentNotes = watch('notes' as any) || '';
    const templateNotes = selectedTemplate?.notes || '';

    if (currentNotes === templateNotes) {
      // If notes are still the template notes, restore to initial notes
      setValue('notes' as any, initialValuesRef.current.notes as any);
    } else {
      // If user has modified the notes, keep their changes
      // Do nothing here - keep the current notes
    }

    // Handle tasks differently based on mode
    if (isEditMode) {
      // In edit mode, we should mark tasks as _destroy: true rather than filtering them out
      setTasks((prevTasks) => {
        const updatedTasks = prevTasks.map((task) => ({
          ...task,
          _destroy: true,
        }));
        return updatedTasks;
      });
    } else {
      // In create mode, we can simply remove all tasks
      setTasks([]);
    }

    setTemplateTagIds([]); // Clear the tracked template tags
    setTemplateAttributeIds([]); // Clear the tracked template attributes
    setSelectedTemplate(null);
    setShowRemoveConfirmation(false);
    resetInitialValues();
  }, [
    watch,
    selectedTemplate,
    setValue,
    templateTagIds,
    templateAttributeIds,
    isEditMode,
    setTasks,
    setTemplateTagIds,
    setTemplateAttributeIds,
    setSelectedTemplate,
    setShowRemoveConfirmation,
    resetInitialValues,
  ]);

  // Add this back, using a one-time initialization
  useEffect(() => {
    // Only initialize on first mount
    resetInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    selectedTemplate,
    showRemoveConfirmation,
    setShowRemoveConfirmation,
    hasExistingData,
    isTemplateModified,
    applyTemplate,
    removeTemplate,
    getActiveTasksCount,
    resetInitialValues,
    isSelectedTemplateAssetDriven,
  };
};
